/**
 * The operator's page: who has an account, and the few things one can need.
 *
 * Reachable only by an account in ADMIN_EMAILS. The engine enforces that; this
 * page hiding itself is a convenience, not the control.
 *
 * Two things an operator deliberately cannot do here, and both are the
 * engine's rules rather than this page's: set someone's password -- reset
 * mails them the same single-use link the forgot-password flow does, so an
 * operator cannot sign in as a user and leave no trace -- and delete their own
 * account, since the only account that could undo it is the one going away.
 */
import { useCallback, useState } from "react";

import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";

function when(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = (Date.now() - d.getTime()) / 86_400_000;
  if (days < 1) return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  if (days < 7) return `${Math.floor(days)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function Admin({ onClose }: { onClose: () => void }) {
  const overview = useAsync((s) => api.adminOverview(s), []);
  const users = useAsync((s) => api.adminUsers(s), []);
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** The account awaiting a typed confirmation before it is removed. */
  const [confirming, setConfirming] = useState<{ id: number; email: string } | null>(null);
  const [typed, setTyped] = useState("");

  const refresh = useCallback(() => {
    overview.refetch();
    users.refetch();
  }, [overview, users]);

  const act = async (id: number, fn: () => Promise<string>) => {
    setBusy(id);
    setNote(null);
    try {
      setNote(await fn());
      refresh();
    } catch (e) {
      setNote((e as Error).message || "That did not work.");
    } finally {
      setBusy(null);
    }
  };

  const o = overview.data;

  return (
    <div className="admin">
      <div className="admin-head">
        <h2>Administration</h2>
        <span className="spacer" />
        <button className="btn" onClick={refresh}>Refresh</button>
        <button className="btn" onClick={onClose}>Close</button>
      </div>

      {note && <div className="admin-note">{note}</div>}

      <div className="kv">
        <Cell k="accounts" v={o ? String(o.users) : "—"} />
        <Cell k="verified" v={o ? String(o.verified) : "—"} />
        <Cell
          k="unverified"
          v={o ? String(o.unverified) : "—"}
          tone={o && o.unverified > 0 ? "warn" : undefined}
        />
        <Cell k="new this week" v={o ? String(o.new_this_week) : "—"} />
        <Cell
          k="locked out"
          v={o ? String(o.locked) : "—"}
          tone={o && o.locked > 0 ? "warn" : undefined}
        />
        <Cell k="active sessions" v={o ? String(o.active_sessions) : "—"} />
        <Cell
          k="email"
          v={o ? (o.mail_configured ? "configured" : "not configured") : "—"}
          tone={o && !o.mail_configured ? "warn" : undefined}
        />
      </div>

      <h3>Accounts</h3>
      {users.loading && <div className="hint"><span className="spinner" /> loading…</div>}
      {users.error && <div className="hint">{users.error.message}</div>}

      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Joined</th>
              <th>Last sign-in</th>
              <th>State</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(users.data?.users ?? []).map((u) => (
              <tr key={u.id}>
                <td className="mono small">
                  {u.email}
                  {u.is_admin && <span className="badge good" style={{ marginLeft: 6 }}>admin</span>}
                </td>
                <td>{u.name ?? "—"}</td>
                <td className="small faint">{when(u.created_at)}</td>
                <td className="small faint">{when(u.last_login)}</td>
                <td className="small">
                  {!u.verified && <span className="badge warn">unverified</span>}
                  {u.locked && <span className="badge bad">locked</span>}
                  {u.verified && !u.locked && <span className="faint">ok</span>}
                </td>
                <td className="num">
                  <div className="admin-actions">
                    {u.locked && (
                      <button
                        className="btn small"
                        disabled={busy === u.id}
                        onClick={() => act(u.id, async () => {
                          await api.adminUnlock(u.id);
                          return `Unlocked ${u.email}.`;
                        })}
                      >
                        Unlock
                      </button>
                    )}
                    <button
                      className="btn small"
                      disabled={busy === u.id}
                      title="Mails them a single-use reset link. You never see the password."
                      onClick={() => act(u.id, async () => {
                        const r = await api.adminReset(u.id);
                        return r.sent
                          ? `Reset link mailed to ${u.email}.`
                          : `Mail is not configured, so nothing was sent. The link is: ${r.link}`;
                      })}
                    >
                      Send reset
                    </button>
                    <button
                      className="btn small danger"
                      disabled={busy === u.id || u.is_admin}
                      title={
                        u.is_admin
                          ? "Remove the address from ADMIN_EMAILS first"
                          : "Delete this account and everything in it"
                      }
                      onClick={() => { setConfirming({ id: u.id, email: u.email }); setTyped(""); }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirming && (
        <div className="modal-backdrop" onClick={(e) => {
          if (e.target === e.currentTarget) setConfirming(null);
        }}>
          <div className="modal" role="dialog" aria-modal="true">
            <h2 className="modal-title">Delete this account?</h2>
            <div className="modal-body">
              <p>
                <b>{confirming.email}</b> and everything it owns — watchlists,
                saved scripts, drawings and sessions — are removed. This cannot
                be undone, and there is no backup to restore from.
              </p>
              {/* Typing the address rather than pressing a second button: the
                  rows look alike, and the cost of deleting the wrong one is
                  total. */}
              <p className="small faint">Type the address to confirm.</p>
              <input
                className="input"
                autoFocus
                value={typed}
                placeholder={confirming.email}
                onChange={(e) => setTyped(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
            <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setConfirming(null)}>Cancel</button>
              <button
                className="btn danger"
                disabled={typed.trim().toLowerCase() !== confirming.email.toLowerCase()}
                onClick={async () => {
                  const target = confirming;
                  setConfirming(null);
                  await act(target.id, async () => {
                    const r = await api.adminDelete(target.id);
                    return `Deleted ${r.deleted}.`;
                  });
                }}
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Cell({ k, v, tone }: { k: string; v: string; tone?: "warn" }) {
  return (
    <div className="kv-cell">
      <div className="k">{k}</div>
      <div className={`v ${tone ?? ""}`}>{v}</div>
    </div>
  );
}
